import { Order } from 'types/OrderTypes';
import { GOADate } from 'utils/date/GOADate';
import { constructPassengers, constructSupplements } from 'utils/Helpers';
import {
  journeyGet as journeyGetRequest,
  journeyGetEarlierDeparturesForLeg,
  journeyGetJourneyPlan,
  journeyGetLaterDeparturesForLeg,
  journeyReplaceLegInJourney,
  offerGetTripPatternInfo,
} from '../../api/v2';
import { configureOpenApiClients } from '../../api/configure';

export const useJourneyApi = (token: string, order: Order) => {
  configureOpenApiClients();

  const journeyGet = async (data: { isDeparture: boolean; date: GOADate }) =>
    await journeyGetRequest({
      headers: { authorization: token ? 'Bearer ' + token : null },
      query: {
        fromId: data.isDeparture ? order.from.id : order.to.id,
        toId: data.isDeparture ? order.to.id : order.from.id,
        isDepartureTime: true,
        dateTimeUtc: data.date ? data.date : data.isDeparture ? order.departureDate : order.returnDate,
        passenger: constructPassengers(order.passengers),
        luggage: constructSupplements(order.supplements),
        includeSupplementProducts: true,
        includeAdditionalOffers: true,
        includePlannedCancellations: true,
        includeRealtimeCancellations: true,
        includeDeparted: true,
        feat: ['SoveProduct'],
      },
    });

  const getLaterDeparturesForLeg = async (data: { id: string; index: number }) =>
    await journeyGetLaterDeparturesForLeg({ path: { tripPatternId: data.id, legIndex: data.index } });

  const getEarlierDeparturesForLeg = async (data: { id: string; index: number }) =>
    await journeyGetEarlierDeparturesForLeg({ path: { tripPatternId: data.id, legIndex: data.index } });

  type ReplaceType = {
    journeyId: string;
    index: number;
    fromId: string;
    toId: string;
    serviceJourneyId: string;
    departureTime: GOADate;
  };

  const getJourneyPlan = async (data: { isDeparture: boolean; date: GOADate }) => {
    return await journeyGetJourneyPlan({
      query: {
        fromId: data.isDeparture ? order.from.id : order.to.id,
        toId: data.isDeparture ? order.to.id : order.from.id,
        isDepartureTime: true,
        dateTimeUtc: data.date ? data.date : data.isDeparture ? order.departureDate : order.returnDate,
        numTripPatterns: 30,
      },
    });
  };

  const replaceLegInJourney = async (data: ReplaceType) => {
    const options = {
      fromId: data.fromId,
      toId: data.toId,
      serviceJourneyId: data.serviceJourneyId,
      departureTime: data.departureTime,
      passenger: constructPassengers(order.passengers),
      luggage: constructSupplements(order.supplements),
    };
    return await journeyReplaceLegInJourney({
      path: { tripPatternId: data.journeyId, legIndex: data.index },
      query: options,
    });
  };

  const getOffers = async (data: { isDeparture: boolean; compartments: number }) => {
    const options = {
      body: {
        passenger: constructPassengers(order.passengers),
        luggage: constructSupplements(order.supplements),
        legs: data.isDeparture ? order.journey.selectedJourney.legs : order.returnJourney.selectedJourney.legs,
        numCompartments: data.compartments,
        mockSleeperSupplement: false,
        includeSleeperProduct: true,
      },
    };
    return await offerGetTripPatternInfo(options);
  };

  return {
    journeyGet,
    getLaterDeparturesForLeg,
    getEarlierDeparturesForLeg,
    replaceLegInJourney,
    getJourneyPlan,
    getOffers,
  };
};
